import { useEffect, useState } from 'react';
import { useSelectedRestaurant } from '../context/SelectedRestaurantContext';
import { getPublicOrderQueue, Order } from '../services/orders';
import { storageService } from '../services/storage';

interface OrderQueueModalProps {
  visible: boolean;
  onClose: () => void;
  restaurantId?: string;
  tableId?: string;
  tableNumber?: number;
}

export default function OrderQueueModal({ visible, onClose, restaurantId, tableId, tableNumber }: OrderQueueModalProps) {
  const { selectedRestaurantId } = useSelectedRestaurant();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [myOrderIds, setMyOrderIds] = useState<string[]>([]);

  const effectiveRestaurantId = restaurantId || selectedRestaurantId || undefined;
  const PUBLIC_ORDER_QUEUE_MY_ORDERS_KEY = 'public_order_queue_my_orders';
  const hasTableContext = Boolean(tableId) || (typeof tableNumber === 'number');
  const isMenuWithoutQr = !hasTableContext;

  const getOrderShortCode = (orderId: string) => {
    if (!orderId) return '';
    const parts = String(orderId).split('-');
    if (parts.length >= 2 && parts[1]) return String(parts[1]).toUpperCase();
    const s = String(orderId);
    return s.length >= 4 ? s.slice(-4).toUpperCase() : s.toUpperCase();
  };

  // Identificar órdenes propias en menú sin QR (mesa virtual #0): usar IDs guardados al crear pedido.
  useEffect(() => {
    if (!visible || !effectiveRestaurantId) return;
    (async () => {
      try {
        const raw = await storageService.getItem(PUBLIC_ORDER_QUEUE_MY_ORDERS_KEY);
        let map: any = {};
        try {
          map = raw ? JSON.parse(raw) : {};
        } catch {
          map = {};
        }
        const v = map?.[effectiveRestaurantId];
        const ids = Array.isArray(v) ? v : (typeof v === 'string' ? [v] : []);
        setMyOrderIds(ids.filter(Boolean));
      } catch {
        setMyOrderIds([]);
      }
    })();
  }, [visible, effectiveRestaurantId]);

  // Barra de progreso eliminada; mantenemos una animación simple para "preparing".

  const ChefIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="w-9 h-9 md:w-11 md:h-11"
      aria-hidden
    >
      {/* Pan base */}
      <defs>
        <linearGradient id="soup" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#FDBA74"/>
          <stop offset="100%" stopColor="#FB923C"/>
        </linearGradient>
      </defs>
      <circle cx="10" cy="12" r="5.2" fill="url(#soup)" stroke="#344155" strokeWidth="1.5" />
      {/* Handle */}
      <rect x="15.2" y="11.2" width="5.2" height="1.6" rx="0.8" fill="#475569" transform="rotate(10 15.2 11.2)" />
      {/* Spoon (stirring) */}
      <g className="chef-stir" transform="translate(10 12)">
        <rect x="-0.4" y="-4.2" width="0.8" height="6.8" rx="0.4" fill="#334155" />
        <circle cx="0" cy="-4.6" r="1.3" fill="#64748B" />
      </g>
      {/* Steam */}
      <path d="M7 6.5c.6-1 .6-1.8 0-2.6" stroke="#CBD5E1" strokeWidth="1.2" fill="none" className="chef-steam-1"/>
      <path d="M10 5.8c.6-1 .6-1.8 0-2.6" stroke="#CBD5E1" strokeWidth="1.2" fill="none" className="chef-steam-2"/>
      <path d="M13 6.2c.6-1 .6-1.8 0-2.6" stroke="#CBD5E1" strokeWidth="1.2" fill="none" className="chef-steam-3"/>
      {/* Flames */}
      <g className="chef-flames">
        <path d="M7 17 l1.2 2 l-2.4 0 z" fill="#F59E0B"/>
        <path d="M10 17 l1.2 2 l-2.4 0 z" fill="#F97316"/>
        <path d="M13 17 l1.2 2 l-2.4 0 z" fill="#F59E0B"/>
      </g>
    </svg>
  );

  // const formatTime = (dateIso?: string): string => {
  //   if (!dateIso) return '';
  //   try {
  //     return new Date(dateIso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  //   } catch {
  //     return '';
  //   }
  // };

  const fetchQueue = async () => {
    if (!effectiveRestaurantId) return;
    try {
      setLoading(true);
      setError(null);
      // Nueva cola pública: endpoint unificado
      const data = await getPublicOrderQueue(effectiveRestaurantId);
      // Soportar respuesta como array directo o como objeto con claves
      const maybeArray: any[] = Array.isArray(data) ? data : [];
      let created = [] as any[];
      let pending = [] as any[];
      let preparing = [] as any[];
      if (maybeArray.length) {
        created = maybeArray.filter(o => o.status === 'created');
        pending = maybeArray.filter(o => o.status === 'pending');
        preparing = maybeArray.filter(o => o.status === 'preparing');
      } else {
        created = data.created || data.data?.created || [];
        pending = data.pending || data.data?.pending || [];
        preparing = data.preparing || data.data?.preparing || [];
      }
      const merged = [...created, ...pending, ...preparing];
      const sorted = merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      setOrders(sorted);
    } catch (e: any) {
      setError(e?.message || 'Error cargando cola de órdenes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) return;
    fetchQueue();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, effectiveRestaurantId]);

  // const highlightedOrderIndex = useMemo(() => {
  //   if (!orders.length) return -1;
  //   const index = orders.findIndex(o => (tableId && o.tableId === tableId) || (tableNumber && o.tableNumber === tableNumber));
  //   return index;
  // }, [orders, tableId, tableNumber]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[70vh] flex flex-col">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-white"
          aria-label="Cerrar"
          title="Cerrar"
        >
          ✕
        </button>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3 pt-12 sm:pt-3">
          <div className="px-2 sm:hidden">
            <div className="flex items-center space-x-2">
              <span className="text-xl">📋</span>
              <h3 className="text-base font-bold text-[#2C4358]">Estado de orden</h3>
            </div>
          </div>
          {loading && (
            <div className="text-center py-6 text-[#85929e] text-sm">Cargando cola...</div>
          )}
          {error && (
            <div className="text-center py-4">
              <div className="inline-flex items-center px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm font-semibold">{error}</div>
            </div>
          )}
          {!loading && !error && orders.length === 0 && (
            <div className="text-center py-8 text-[#85929e] text-sm">No hay órdenes en la cola aún.</div>
          )}

          {!loading && !error && orders.length > 0 && (
            <ul className="space-y-3">
              {orders.map((o, idx) => {
                const isMine = hasTableContext
                  ? ((tableId && o.tableId === tableId) || (typeof tableNumber === 'number' && o.tableNumber === tableNumber))
                  : (myOrderIds.includes(o.id));
                const isVirtualTableZero = parseInt(String(o.tableNumber ?? ''), 10) === 0;
                return (
                  <li key={o.id} className={`flex items-center justify-between p-3 rounded-xl border shadow-sm ${
                    isMine ? 'bg-[#22B6B2]/10 border-[#22B6B2]/30' : 'bg-white border-gray-100'
                  } ${o.status === 'preparing' ? 'ring-2 ring-amber-300 queue-pulse' : ''}`}>
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-sm font-black ${
                        o.status === 'ready' ? 'bg-green-100 text-green-700' : 
                        o.status === 'preparing' ? 'bg-yellow-100 text-yellow-700' : 
                        o.status === 'created' ? 'bg-orange-100 text-orange-700' : 
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-[#2C4358]">
                          {isVirtualTableZero ? `#${getOrderShortCode(o.id)}` : `Mesa ${o.tableNumber ?? '-'}`}
                        </div>
                        <div className="text-xs font-semibold text-[#85929e] flex items-center gap-1">
                          {o.status === 'ready' ? 'Lista' : 
                           o.status === 'preparing' ? 'En proceso' : 
                           o.status === 'pending' ? 'Pendiente' : 
                           o.status === 'created' ? 'Creada' : 
                           o.status}
                          {o.status === 'preparing' && <ChefIcon />}
                        </div>
                        {/* Hora oculta */}
                        {/* Sin barra de progreso; chef animado para indicar actividad */}
                      </div>
                    </div>
                    <div className="text-right">
                      {isMine && (
                        <div className="inline-flex items-center px-2 py-1 rounded-lg bg-[#22B6B2]/20 text-[#2C4358] text-xs font-bold">
                          {isMenuWithoutQr ? 'Tu pedido' : 'Tu mesa'}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer minimal */}
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <div className="text-[11px] text-[#85929e]">Pulsa "Actualizar" para refrescar</div>
          <button onClick={fetchQueue} className="px-3 py-2 rounded-lg text-xs font-bold bg-[#f7ebd6] border border-[#f7ebd6]/50 hover:bg-[#f0e0c0] text-[#2C4358]">Actualizar</button>
        </div>
      </div>
      <style>{`
        @keyframes queueShimmer {0%{background-position:0% 0}100%{background-position:200% 0}}
        @keyframes stir {0%{transform:rotate(-18deg)}50%{transform:rotate(18deg)}100%{transform:rotate(-18deg)}}
        @keyframes steamRise {0%{opacity:.6; transform:translateY(3px)}100%{opacity:0; transform:translateY(-6px)}}
        @keyframes flame {0%,100%{transform:scaleY(.9); opacity:.9}50%{transform:scaleY(1.1); opacity:1}}
        @keyframes ringPulse {0%{box-shadow:0 0 0 0 rgba(245,158,11,.35)}70%{box-shadow:0 0 0 8px rgba(245,158,11,0)}100%{box-shadow:0 0 0 0 rgba(245,158,11,0)}}
        .chef-stir{transform-origin:0px 0px; animation:stir .9s ease-in-out infinite}
        .chef-steam-1{animation:steamRise 1.2s ease-in-out infinite}
        .chef-steam-2{animation:steamRise 1.4s ease-in-out infinite}
        .chef-steam-3{animation:steamRise 1.1s ease-in-out infinite}
        .chef-flames path{animation:flame .6s ease-in-out infinite}
        .queue-pulse{animation:ringPulse 1.4s ease-out infinite}
      `}</style>
    </div>
  );
}


